import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import Swal from 'sweetalert2';
import { DeliveryService } from '../deliveries/delivery.service';
import { ClientMessageDialogComponent } from './client-message-dialog/client-message-dialog.component';

interface LogisticsClient {
  contactNumber: string;
  normalizedContactNumber: string;
  customerName: string;
  totalOrders: number;
  activeOrders: number;
  paidOrders: number;
  totalSpend: number;
  lastOrderDate?: string;
  lastPickupLocation?: string;
  lastDeliveryLocation?: string;
  lastDriverName?: string;
  lastVehicleNumber?: string;
}

@Component({
  selector: 'app-logistics-clients',
  templateUrl: './logistics-clients.component.html',
  styleUrls: ['./logistics-clients.component.scss']
})
export class LogisticsClientsComponent implements OnInit {
  displayedColumns: string[] = [
    'customerName',
    'contactNumber',
    'totalOrders',
    'activeOrders',
    'paidOrders',
    'totalSpend',
    'lastOrderDate',
    'lastRoute',
    'actions'
  ];

  dataSource = new MatTableDataSource<LogisticsClient>([]);
  loading = false;
  search = '';
  selectedClient: LogisticsClient | null = null;
  clientOrders: any[] = [];
  loadingOrders = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private deliveryService: DeliveryService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadClients();
  }

  loadClients(): void {
    this.loading = true;
    this.deliveryService.getClients(this.search).subscribe({
      next: (clients) => {
        this.dataSource.data = clients || [];
        if (this.paginator) this.dataSource.paginator = this.paginator;
        if (this.sort) this.dataSource.sort = this.sort;
        this.loading = false;
      },
      error: (err) => {
        console.error('[LogisticsClients] ❌ Failed to load clients:', err);
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load logistics clients.', 'error');
      }
    });
  }

  applySearch(event?: Event): void {
    if (event) {
      this.search = ((event.target as HTMLInputElement)?.value || '').trim();
    }
    this.loadClients();
  }

  resetSearch(): void {
    this.search = '';
    this.loadClients();
  }

  openHistory(client: LogisticsClient): void {
    this.selectedClient = client;
    this.loadingOrders = true;
    this.clientOrders = [];

    this.deliveryService.getOrdersByContact(client.contactNumber).subscribe({
      next: (orders) => {
        this.clientOrders = orders || [];
        this.loadingOrders = false;
      },
      error: (err) => {
        console.error('[LogisticsClients] ❌ Failed to load client history:', err);
        this.loadingOrders = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load client order history.', 'error');
      }
    });
  }

  async sendMessage(client: LogisticsClient): Promise<void> {
    const dialogRef = this.dialog.open(ClientMessageDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      panelClass: 'app-modal-panel',
      backdropClass: 'app-modal-backdrop',
      data: { client }
    });

    dialogRef.afterClosed().subscribe((payload) => {
      if (!payload) return;

      this.deliveryService.sendClientMessage(payload).subscribe({
        next: (result) => {
          const smsText = result.smsRequested ? (result.smsSent ? 'SMS sent' : 'SMS failed') : 'SMS skipped';
          const emailText = result.emailRequested ? (result.emailQueued ? 'Email queued' : 'Email failed') : 'Email skipped';
          Swal.fire('Message Sent', `${smsText}. ${emailText}.`, 'success');
        },
        error: (err) => {
          console.error('[LogisticsClients] ❌ Failed to send client message:', err);
          Swal.fire('Error', err?.error?.message || 'Failed to send client message.', 'error');
        }
      });
    });
  }
}
